import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';

import { IAccount, IInboxSyncState, MailboxType } from '../../../models/account.models';
import { IConversation, IGetConversationsParams, ConversationClassification, ConversationStatus, ISentMessage, IGetSentMessagesParams } from '../../../models/conversation.models';
import { ComposeType } from '../../../models/compose.models';
import { IDraft, IGetDraftsParams } from '../../../models/draft.models';
import { IMention } from '../../../models/mention.models';
import { DEFAULT_MAILBOX_TAG_DISPLAY, InboxFolderType, MailboxTagDisplay } from '../../../models/inbox.models';
import { MailboxTagPreferenceService } from '../../../services/mailbox-tag-preference.service';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { DragToScrollDirective } from 'src/app/shared/drag-scroll.directive';
import { ComposeService } from '../../../services/compose.service';
import { AppState } from 'src/app/core/app.state';
import { IPagination } from 'src/app/shared/models/page.model';
import * as smartInboxActions from '../../../store/smart-inbox.actions';
import * as smartInboxSelectors from '../../../store/smart-inbox.selectors';
import { IInboxFilter } from '../../../store/smart-inbox.reducers';
import { ConversationAdvancedFilterComponent, IAdvancedFilter } from './conversation-advanced-filter/conversation-advanced-filter.component';
import { HighlightPipe } from 'src/app/core/pipes/highlight.pipe';
import { TranslateService } from '@ngx-translate/core';
import { getUserInitials, getUserColor } from '../../../utils/user.util';
import { classificationLabelKey, orderCategories } from '../../../utils/classification.util';
import { buildMailboxTag, IMailboxTag } from '../../../utils/mailbox-tag.util';
import { MailboxTagColor } from '../../../utils/user.util';
import { buildConversationListParams, buildSentMessagesParams, hasNoListableMailbox, listableMailboxIds } from '../../../utils/conversation-params.util';
import { NO_SELECTION_MODIFIERS, SelectionModifiers } from 'src/app/shared/models/selection.models';
import { RangeSelectionTracker } from 'src/app/shared/utils/range-selection';

/**
 * Scrollable list of email conversations with search, bulk selection, keyboard
 * navigation, and infinite-scroll pagination.
 */
@Component({
  selector: 'app-conversation-list',
  standalone: true,
  templateUrl: './conversation-list.component.html',
  styleUrls: ['./conversation-list.component.scss'],
  imports: [CommonModule, UiModule, TranslateModule, SharedModule, DragToScrollDirective, MatMenuModule, MatIconModule, ConversationAdvancedFilterComponent, HighlightPipe],
})
export class ConversationListComponent implements OnInit, OnDestroy {

  // ── Decorated properties ───────────────────────────────────────────────────

  @ViewChild('advancedFilterTrigger') advancedFilterTrigger?: MatMenuTrigger;
  @ViewChild('conversationContainer') conversationContainerRef: ElementRef<HTMLElement> | null = null;
  @ViewChildren('conversationRow') conversationRows!: QueryList<ElementRef<HTMLElement>>;

  // ── Public properties ──────────────────────────────────────────────────────

  conversations: IConversation[] = [];
  activeConversation: IConversation | null = null;
  selectedConversationIds: number[] = [];

  /**
   * Range pivot for shift-click. Only the pivot lives here — the selection
   * itself is store state, so this stays a bare tracker rather than a
   * SelectionController, which would keep a second copy of the ids.
   */
  private readonly rangeSelection = new RangeSelectionTracker();

  /** Ids of the rows last rendered, used to spot a real list change. */
  private renderedIds: number[] = [];
  /** A `/threads` request is in flight. Not the mailbox sync — see `syncState`. */
  isLoading = false;
  /** Server-side mailbox sync state of the accounts in view (`sync_status`). */
  syncState: IInboxSyncState = { status: 'idle', error: null, emails: [] };
  /** A sync this component asked for is still awaiting its response. */
  isSyncRequestInFlight = false;
  isFilterApplied = false;
  activeAccounts: IAccount[] = [];
  /** Show the recipient-mailbox tag on every card (2+ mailboxes connected). */
  mailboxTagAlwaysVisible = false;
  /** Tint per mailbox id, unique across the connected accounts. */
  mailboxTagColors: Record<number, MailboxTagColor> = {};
  /** The user's Settings choice for where the tag may appear. */
  mailboxTagDisplay: MailboxTagDisplay = DEFAULT_MAILBOX_TAG_DISPLAY;
  /**
   * Tag per conversation id, rebuilt only when one of its inputs changes.
   *
   * Not a template call: this list has no OnPush and no virtual scroll, pages
   * accumulate 50 at a time, and SSE keeps change detection busy — so a getter here
   * meant a unicode regex, a string hash and an object allocation per row per tick.
   */
  private mailboxTags = new Map<number, IMailboxTag | null>();
  /** Same, for the Sent list — keyed on sent-message id, which is a different space. */
  private sentMailboxTags = new Map<number, IMailboxTag | null>();
  isLoadingMore = false;
  activeFolder: InboxFolderType = InboxFolderType.ALL;
  drafts: IDraft[] = [];
  mentions: IMention[] = [];
  sentMessages: ISentMessage[] = [];

  readonly InboxFolderType = InboxFolderType;
  readonly getUserInitials = getUserInitials;
  readonly getUserColor = getUserColor;
  readonly classificationLabelKey = classificationLabelKey;

  searchInput = new FormControl('');

  // ── Private properties ─────────────────────────────────────────────────────

  filter: IInboxFilter = {};

  private page = 1;
  private limit = 50;
  private totalPages = 1;
  private accounts: IAccount[] = [];
  private destroy$ = new Subject<void>();

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private composeService: ComposeService,
    private authService: AuthService,
    private translate: TranslateService,
    private mailboxTagPreference: MailboxTagPreferenceService,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.store.select(smartInboxSelectors.selectConversations)
      .pipe(takeUntil(this.destroy$))
      .subscribe((conversations) => {
        this.conversations = conversations;
        this.rebuildMailboxTags();

        // The pivot is an index, so any reorder or page change invalidates it.
        const ids = conversations.map((c) => c.id);
        const changed = ids.length !== this.renderedIds.length || ids.some((id, i) => id !== this.renderedIds[i]);
        this.renderedIds = ids;
        if (changed) this.rangeSelection.reset();
      });

    this.store.select(smartInboxSelectors.selectActiveConversation)
      .pipe(takeUntil(this.destroy$))
      .subscribe((conversation) => { this.activeConversation = conversation; });

    this.store.select(smartInboxSelectors.selectSelectedConversationIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ids) => { this.selectedConversationIds = ids; });

    this.store.select(smartInboxSelectors.selectGetLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.isLoading = loading.conversations || loading.mentions;
      });

    this.store.select(smartInboxSelectors.selectInboxSyncState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((syncState) => { this.syncState = syncState; });

    this.store.select(smartInboxSelectors.selectUpdateLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => { this.isSyncRequestInFlight = loading.accountRefresh; });

    this.store.select(smartInboxSelectors.selectIsFilterApplied)
      .pipe(takeUntil(this.destroy$))
      .subscribe((applied) => { this.isFilterApplied = applied; });

    this.store.select(smartInboxSelectors.selectFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe((filter) => {
        this.filter = filter;
        const active = filter.activeAccounts ?? [];
        this.activeAccounts = active.length > 0 ? active : this.accounts;
      });

    this.store.select(smartInboxSelectors.selectAccounts)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => {
        this.accounts = accounts;
        if ((this.filter.activeAccounts ?? []).length === 0) {
          this.activeAccounts = accounts;
        }
      });

    this.store.select(smartInboxSelectors.selectMailboxTagAlwaysVisible)
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => {
        this.mailboxTagAlwaysVisible = visible;
        this.rebuildMailboxTags();
      });

    this.store.select(smartInboxSelectors.selectMailboxTagColors)
      .pipe(takeUntil(this.destroy$))
      .subscribe((colors) => {
        this.mailboxTagColors = colors;
        this.rebuildMailboxTags();
      });

    this.mailboxTagPreference.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe((display) => {
        this.mailboxTagDisplay = display;
        this.rebuildMailboxTags();
      });

    this.store.select(smartInboxSelectors.selectPagination)
      .pipe(takeUntil(this.destroy$))
      .subscribe((pagination) => {
        if (pagination) {
          this.totalPages = pagination.pages ?? 1;
          this.page = pagination.page ?? 1;
        } else {
          this.page = 1;
          this.totalPages = 1;
        }
      });

    this.store.select(smartInboxSelectors.selectActiveFolder)
      .pipe(takeUntil(this.destroy$))
      .subscribe((folder) => { this.activeFolder = folder; });

    this.store.select(smartInboxSelectors.selectDrafts)
      .pipe(takeUntil(this.destroy$))
      .subscribe((drafts) => { this.drafts = drafts; });

    this.store.select(smartInboxSelectors.selectMentions)
      .pipe(takeUntil(this.destroy$))
      .subscribe((mentions) => { this.mentions = mentions; });

    this.store.select(smartInboxSelectors.selectSentMessages)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sentMessages) => {
        this.sentMessages = sentMessages;
        this.rebuildMailboxTags();
      });

    this.searchInput.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value) => {
        const searchTerm = value?.trim() ?? '';
        this.page = 1;
        this.store.dispatch(smartInboxActions.SetFilter({ filter: { ...this.filter, searchTerm } }));
      });

    // Scroll to top when filter changes (not on pagination)
    this.actions$.pipe(
      ofType(smartInboxActions.SetFilter),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.scrollToTop();
    });

    this.actions$.pipe(
      ofType(
        smartInboxActions.LoadMoreConversationsSuccess,
        smartInboxActions.LoadMoreConversationsError,
        smartInboxActions.LoadMoreDraftsSuccess,
        smartInboxActions.LoadMoreDraftsError,
        smartInboxActions.LoadMoreMentionsSuccess,
        smartInboxActions.LoadMoreMentionsError,
        smartInboxActions.LoadMoreSentMessagesSuccess,
        smartInboxActions.LoadMoreSentMessagesError,
      ),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.isLoadingMore = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Computed (template getters) ────────────────────────────────────────────

  /**
   * True while the *server* reports mail being fetched. Independent of `isLoading` (a
   * `/threads` GET) and deliberately not OR'd with `isSyncRequestInFlight`: a queued
   * request is not a sync, and a refused one (`queue_full`, `not_connected`) would show a
   * "syncing" banner for a sync that never started. The gap between the POST and its 202
   * is covered by the reducer mirroring the route's own claim.
   */
  get isSyncing(): boolean {
    return this.syncState.status === 'syncing';
  }

  get hasSyncError(): boolean {
    return !this.isSyncing && this.syncState.status === 'error';
  }

  /** "Sync now" is unavailable while syncing *or* while our own request is in flight. */
  get isSyncRequestBlocked(): boolean {
    return this.isSyncing || this.isSyncRequestInFlight;
  }

  /** Mailboxes named in the sync banner, e.g. "a@x.com, b@y.com". */
  get syncStateEmails(): string {
    return this.syncState.emails.join(', ');
  }

  get isAllConversationsSelected(): boolean {
    // By id, not by count: a length check calls a selection "all" whenever it
    // happens to match the page size, which range selection makes easy to hit.
    return this.conversations.length > 0 && this.conversations.every((c) => this.selectedConversationIds.includes(c.id));
  }

  get isPartialSelectedAll(): boolean {
    return this.selectedConversationIds.length > 0 && !this.isAllConversationsSelected;
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  onConversationSelected(conversation: IConversation): void {
    this.store.dispatch(smartInboxActions.SelectConversation({ conversation }));
  }

  onConversationToggled(conversation: IConversation, checked: boolean, modifiers: SelectionModifiers = NO_SELECTION_MODIFIERS): void {
    const rows = this.conversations;
    const affected = this.rangeSelection.resolve(rows, rows.indexOf(conversation), modifiers);

    this.store.dispatch(
      smartInboxActions.SetConversationsSelection({
        conversationIds: affected.map((c) => c.id),
        selected: checked,
      }),
    );
  }

  onAllConversationsToggled(): void {
    // Not a row click, so it must not leave a stale pivot behind.
    this.rangeSelection.reset();
    this.store.dispatch(smartInboxActions.ToggleAllConversationsSelection());
  }

  /**
   * "Sync now" from the empty-state banner. Queues a real mailbox sync for the accounts in
   * view; the list fills from realtime events and reloads when the sync ends.
   *
   * This used to re-issue `LoadConversations` only, so a banner that reads as a sync button
   * could never produce mail no matter how often it was clicked.
   */
  onSyncRequested(): void {
    if (this.isSyncRequestBlocked) return;

    const accountsIds = this.activeAccounts
      .filter((account) => account.id !== -1)
      .map((account) => account.id);
    if (!accountsIds.length) return;

    this.page = 1;
    this.store.dispatch(smartInboxActions.RefreshAccounts({ accountsIds }));
  }

  onScroll(event: Event): void {
    if (this.isLoading || this.isLoadingMore) return;

    const container = event.target as HTMLElement | null;
    if (!container) return;

    const thresholdPx = 80;
    const isNearBottom =
      container.scrollTop + container.clientHeight >= container.scrollHeight - thresholdPx;

    if (isNearBottom && this.page < this.totalPages) {
      // Conversations and Sent are the mailbox-filtered lists, where an empty `mailbox_ids`
      // is read by the backend as EVERY accessible mailbox. A disconnect deliberately does
      // not reload the list, so page/totalPages stay stale — without this the next scroll
      // pages in mail from the mailboxes just filtered out. Nothing is dispatched: the
      // loaded threads stay readable, exactly as after the disconnect itself.
      const isMailboxFiltered = this.activeFolder !== InboxFolderType.DRAFTS
        && this.activeFolder !== InboxFolderType.MENTIONS;
      if (isMailboxFiltered && hasNoListableMailbox(this.activeAccounts)) return;

      this.isLoadingMore = true;
      this.page = this.page + 1;

      if (this.activeFolder === InboxFolderType.DRAFTS) {
        this.store.dispatch(smartInboxActions.LoadMoreDrafts({
          params: this.getDraftsParams(this.page),
        }));
      } else if (this.activeFolder === InboxFolderType.MENTIONS) {
        this.store.dispatch(smartInboxActions.LoadMoreMentions({
          params: this.getMentionsParams(this.page),
        }));
      } else if (this.activeFolder === InboxFolderType.SENT) {
        this.store.dispatch(smartInboxActions.LoadMoreSentMessages({
          params: this.getSentMessagesParams(this.page),
        }));
      } else {
        this.store.dispatch(smartInboxActions.LoadMoreConversations({
          params: this.getConversationsParams(this.page),
        }));
      }
    }
  }

  onConversationListKeydown(event: KeyboardEvent): void {
    if (this.conversations.length === 0) return;

    const isArrowDown = event.key === 'ArrowDown';
    const isArrowUp = event.key === 'ArrowUp';
    if (!isArrowDown && !isArrowUp) return;

    event.preventDefault();

    const currentIndex = this.activeConversation
      ? this.conversations.findIndex((c) => c.id === this.activeConversation!.id)
      : -1;

    let nextIndex: number;
    if (isArrowDown) {
      nextIndex = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, this.conversations.length - 1);
    } else {
      nextIndex = currentIndex < 0 ? this.conversations.length - 1 : Math.max(0, currentIndex - 1);
    }

    if (currentIndex === nextIndex) return;

    const nextConversation = this.conversations[nextIndex];
    if (nextConversation) {
      this.store.dispatch(smartInboxActions.SelectConversation({ conversation: nextConversation }));
      this.scrollConversationIntoView(nextIndex);
    }
  }

  onAdvancedFilterApply(advancedFilter: IAdvancedFilter): void {
    this.advancedFilterTrigger?.closeMenu();
    this.page = 1;
    this.store.dispatch(smartInboxActions.SetFilter({
      filter: {
        ...this.filter,
        activeAccounts: advancedFilter.activeAccounts,
        categories: advancedFilter.categories,
        statuses: advancedFilter.statuses,
        assigneeIds: advancedFilter.assigneeIds,
        teamIds: advancedFilter.teamIds,
        from: advancedFilter.from,
        to: advancedFilter.to,
        subject: advancedFilter.subject,
        includesWords: advancedFilter.includesWords,
        excludesWords: advancedFilter.excludesWords,
        dateFrom: advancedFilter.dateFrom,
        dateTo: advancedFilter.dateTo,
        hasAttachment: advancedFilter.hasAttachment,
        isUrgent: advancedFilter.isUrgent,
        customerIds: advancedFilter.customerIds,
        selectedCustomers: advancedFilter.selectedCustomers,
      },
    }));
  }

  onAdvancedFilterClear(): void {
    this.page = 1;
    // Reset everything the filter panel owns; keep only the free-text search term.
    this.store.dispatch(smartInboxActions.SetFilter({ filter: { searchTerm: this.filter.searchTerm } }));
  }

  get hasActiveAdvancedFilter(): boolean {
    const f = this.filter;
    return !!(
      f.categories?.length || f.statuses?.length || f.assigneeIds?.length || f.teamIds?.length ||
      f.from || f.to || f.subject || f.includesWords || f.excludesWords ||
      f.dateFrom || f.dateTo || f.hasAttachment || f.isUrgent || f.customerIds?.length
    );
  }

  // ── Draft handlers ──────────────────────────────────────────────────────

  openMention(mention: IMention): void {
    this.store.dispatch(smartInboxActions.SelectConversationFromComment({ mention }));
  }

  trackByMentionId(_index: number, mention: IMention): number {
    return mention.id;
  }

  // ── Sent message handlers ───────────────────────────────────────────────

  openSentMessage(message: ISentMessage): void {
    this.store.dispatch(smartInboxActions.SelectSentMessage({ message }));
  }

  trackBySentMessageId(_index: number, message: ISentMessage): number {
    return message.id;
  }

  // ─── Recipient mailbox tag ──────────────────────────────────────────────

  /** Recipient-mailbox tag for a card, or `null` when it must stay hidden. */
  mailboxTagFor(conversation: IConversation): IMailboxTag | null {
    return this.mailboxTags.get(conversation.id) ?? null;
  }

  /** Recipient-mailbox tag for a Sent row, or `null` when it must stay hidden. */
  sentMailboxTagFor(message: ISentMessage): IMailboxTag | null {
    return this.sentMailboxTags.get(message.id) ?? null;
  }

  /**
   * Rebuilds every card's tag. Called whenever the conversations, the sent messages,
   * the tints, the in-view threshold or the user's preference change — the only inputs.
   *
   * Both lists follow the same visibility rules: with one mailbox in view every row
   * belongs to it, in Sent as much as in Conversations, so the tag would only repeat.
   */
  private rebuildMailboxTags(): void {
    const options = {
      enabled: this.mailboxTagPreference.showOnList(),
      alwaysVisible: this.mailboxTagAlwaysVisible,
      colors: this.mailboxTagColors,
    };

    const next = new Map<number, IMailboxTag | null>();
    for (const conversation of this.conversations) {
      next.set(conversation.id, buildMailboxTag(conversation.mailbox, options));
    }
    this.mailboxTags = next;

    const nextSent = new Map<number, IMailboxTag | null>();
    for (const message of this.sentMessages) {
      nextSent.set(message.id, buildMailboxTag(message.mailbox, options));
    }
    this.sentMailboxTags = nextSent;
  }

  // ─── Classification tags ────────────────────────────────────────────────

  /**
   * Categories the thread carries, most recent first. Falls back to the single
   * `classification` field for threads the rollup hasn't been computed for.
   */
  private getCategories(conversation: IConversation): ConversationClassification[] {
    if (conversation.categories?.length) return orderCategories(conversation.categories);
    return conversation.classification ? [conversation.classification] : [];
  }

  /** The one category shown on the card. */
  getPrimaryCategory(conversation: IConversation): ConversationClassification | null {
    return this.getCategories(conversation)[0] ?? null;
  }

  /** How many categories are hidden behind the "+n" chip (0 = no chip). */
  getExtraCategoryCount(conversation: IConversation): number {
    return Math.max(0, this.getCategories(conversation).length - 1);
  }

  /** Tooltip listing the categories collapsed into the "+n" chip. */
  getExtraCategoriesLabel(conversation: IConversation): string {
    return this.getCategories(conversation)
      .slice(1)
      .map((category) => this.translate.instant(classificationLabelKey(category)))
      .join(', ');
  }

  getMentionAuthorInitials(mention: IMention): string {
    return getUserInitials({ name: mention.author.name });
  }

  getMentionAuthorColor(mention: IMention): string {
    return getUserColor({ name: mention.author.name });
  }

  /** Returns HTML for the first line: "Author mentioned you" or "You mentioned yourself". */
  getMentionDescription(mention: IMention): string {
    const currentUser = this.authService.getCurrentUser();
    const isSelf = currentUser && (
      currentUser.email === mention.author.email ||
      (currentUser as any).name === mention.author.name
    );
    if (isSelf) {
      const you = this.translate.instant('smart_inbox.inbox_view.conversation_list.mention_you');
      const yourself = this.translate.instant('smart_inbox.inbox_view.conversation_list.mention_yourself');
      const mentioned = this.translate.instant('smart_inbox.inbox_view.conversation_list.mention_mentioned');
      return `<strong>${you}</strong> ${mentioned} <strong>${yourself}</strong>`;
    }
    const mentionedYou = this.translate.instant('smart_inbox.inbox_view.conversation_list.mention_mentioned_you');
    const author = mention.author.name;
    return `<strong>${author}</strong> ${mentionedYou}`;
  }

  openDraft(draft: IDraft): void {
    this.composeService.openFromDraft(draft);
  }

  trackByDraftId(_index: number, draft: IDraft): number {
    return draft.id;
  }

  getComposeTypeLabel(type: ComposeType): string {
    switch (type) {
      case ComposeType.REPLY: return 'Reply';
      case ComposeType.REPLY_ALL: return 'Reply All';
      case ComposeType.FORWARD: return 'Forward';
      default: return 'New Email';
    }
  }

  getComposeTypeIcon(type: ComposeType): string {
    switch (type) {
      case ComposeType.REPLY: return 'reply';
      case ComposeType.REPLY_ALL: return 'reply-all';
      case ComposeType.FORWARD: return 'forward';
      default: return 'inbox';
    }
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  isSharedInboxOnlyActive(): boolean {
    return this.activeAccounts.every((i) => i.mailbox_type === MailboxType.SHARED);
  }

  trackByConversationId(_index: number, conversation: IConversation): number {
    return conversation.id;
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private scrollToTop(): void {
    const container = this.conversationContainerRef?.nativeElement;
    if (container) container.scrollTop = 0;
  }

  private scrollConversationIntoView(index: number): void {
    setTimeout(() => {
      const container = this.conversationContainerRef?.nativeElement;
      const rowEl = this.conversationRows.toArray()[index]?.nativeElement;
      if (!container || !rowEl) return;

      const rowTop = rowEl.offsetTop;
      const rowHeight = rowEl.offsetHeight;
      const minScrollTop = Math.max(0, rowTop + rowHeight - container.clientHeight);
      const maxScrollTop = rowTop;
      const newScrollTop = Math.min(maxScrollTop, Math.max(0, minScrollTop));

      if (newScrollTop !== container.scrollTop) {
        container.scrollTop = newScrollTop;
      }
    }, 0);
  }

  // Page 2 must ask for the same mailboxes page 1 did.
  private getConversationsParams(page: number): IGetConversationsParams {
    return buildConversationListParams(this.filter, listableMailboxIds(this.activeAccounts), page, this.limit);
  }

  // Deliberately unfiltered: drafts are local records, still the user's to read and edit.
  private getDraftsParams(page: number): IGetDraftsParams {
    return {
      mailbox_ids: this.activeAccounts.map((i) => i.id),
      page,
      limit: this.limit,
    };
  }

  private getMentionsParams(page: number): { page: number; limit: number } {
    return { page, limit: this.limit };
  }

  private getSentMessagesParams(page: number): IGetSentMessagesParams {
    return buildSentMessagesParams(this.filter, listableMailboxIds(this.activeAccounts), page, this.limit);
  }
}
